import React, { useState, useCallback, useEffect } from 'react';
import {
  Button, Typography, Box, Chip, Tooltip, IconButton, Modal, TextField, Dialog,
  TableContainer, Table, TableHead, TableRow, TableBody, TablePagination, TableCell
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Colors } from '../../../../Styles/Colors';
import { ImagePaths } from '../../../../Utility/ImagePaths';
import { DateTime } from 'luxon';
import RestAPIService from '../../../../Utility/Services/RestAPIService';
import { Serviceurls } from '../../../../Utility/API/Serviceurls';
import { DeletePopup } from '../../../../Components/Common Components/CommonComponents';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import { painLevelTxt } from '../../../../Utility/Constants';
import SarasToolsFieldWithMic from './SarasToolsFieldWithMic';
import { formatDate } from '../../../../Components/CommonFunctions/CommonFunctions';


const instructions = (item) => {
  let value = '';
  switch (item) {
    case 'Before Food':
      value = 'BF'
      break;
    case 'After Food':
      value = 'AF'
      break;
    case 'External Use':
      value = 'EU'
      break;
    case 'Empty Stomach':
      value = 'ES'
      break;
    case 'With Food':
      value = 'WF'
      break;
    default:
      value = item;
      break;
  }
  return value;
}

const formatValue = (val) => {
  if (!val) return "-";
  try {
    if (typeof val === "string") {
      const dt = DateTime.fromISO(val);
      if (dt.isValid) {
        if (val.includes("T")) {
          const trimmed = val.slice(0, 23);
          const dt = DateTime.fromISO(trimmed);
          const formatted = dt.toFormat("dd-MM-yyyy hh:mm a");
          return formatted;
        }
        return formatDate(val);
      }
    }
  } catch {
    // fallback
  }
  return val;
};

const getTruncatedValue = (value, maxLength) => {
  if (!value) return '-';
  return value?.length > maxLength ? (
    <Tooltip placement="top" title={value} arrow>
      <div style={{fontSize: "0.8vw"}}>{value.slice(0, maxLength) + "..."}</div>
    </Tooltip>
  ) : (
    value
  );
};

const tblPrescripCol = [
  {
    field: 'drug_name', headerName: 'Drug Details', flex: 0.45,
    renderCell: ({ row }) => (
      <Box>
        {row?.drug_name ? (
          <Box component="div">
            <Typography fontSize="0.9vw" fontWeight={600}>
              {row?.drug_name?.length > 15 ? (
                <Tooltip placement="top" title={row?.drug_name}>
                  <div>{row?.drug_name.slice(0, 15) + '...'}</div>
                </Tooltip>
              ) : (
                row?.drug_name
              )}
            </Typography>
            <Typography display="inline-flex" fontSize="0.8vw" color={Colors.SecondaryText}>
              {row?.generic_name ? (
                row.generic_name?.length > 15 ? (
                  <Tooltip placement="top" title={row.generic_name}>
                    <div>{row.generic_name.slice(0, 15) + '...'}</div>
                  </Tooltip>
                ) : (
                  row.generic_name
                )
              ) : (
                '-'
              )} | {row?.drug_strength || '-'} {row?.drug_type || ''}
            </Typography>
          </Box>
        ) : (
          '-'
        )}
      </Box>
    )
  },

  // Conditional M-A-E-N Column
  {
    field: 'instructions', headerName: 'M-A-E-N', flex: 0.2,
    renderCell: ({ row }) => (
      <Box component="div">
        {Array.isArray(row?.tapper_meds) && row.tapper_meds?.length > 0 ? (
          row.tapper_meds.map((tapList, tapIndex) => {
            const mean = `${tapList?.morning || '0'} - ${tapList?.afternoon || '0'} - ${tapList?.evening || '0'} - ${tapList?.night || '0'}`;
            return (
              <Box key={tapIndex} mb="0.2dvw">
                {mean?.length > 20 ? (
                  <Tooltip placement="top" title={mean}>
                    <Typography fontSize="0.8dvw" fontWeight={600}>{mean?.slice(0, 20) + '...'}</Typography>
                  </Tooltip>
                ) : (
                  <Typography fontSize="0.8dvw" fontWeight={600}>{mean}</Typography>
                )}
                <Typography fontSize="0.8dvw" color={Colors.SecondaryText}>
                  {instructions(tapList?.instructions)}
                </Typography>
              </Box>
            );
          })
        ) : (
          <Box>
            <Typography fontSize={row?.is_unit_ml ? '0.8vw' : '0.9vw'} fontWeight={600}>
              {`${row?.morning || '0'}${row.is_unit_ml ? 'ML' : ''} - ${row?.afternoon || '0'}${row.is_unit_ml ? 'ML' : ''} - ${row?.evening || '0'}${row.is_unit_ml ? 'ML' : ''} - ${row?.night || '0'}${row.is_unit_ml ? 'ML' : ''}`}
            </Typography>
            <Typography fontSize="0.8vw" color={Colors.SecondaryText}>
              {instructions(row?.instructions)}
            </Typography>
          </Box>
        )}
      </Box>
    )
  },

  // Conditional Days Column
  {
    field: 'duration', headerName: 'Days', flex: 0.2,
    renderCell: ({ row }) => (
      <Box>
        {Array.isArray(row?.tapper_meds) && row.tapper_meds?.length > 0 ? (
          row.tapper_meds.map((tapList, tapIndex) => (
            <Box key={tapIndex} mb="0.2dvw">
              <Typography fontSize="0.8dvw" visibility="hidden">placeholder</Typography>
              <Typography fontSize="0.8dvw" fontWeight={600}>
                {tapList?.duration || '-'}
              </Typography>
              <Typography fontSize="0.8dvw">
                {tapList?.no_of_dosage || '-'}
              </Typography>
            </Box>
          ))
        ) : (
          <Typography>
            {row?.duration || ''} {row?.duration_mode || ''}
          </Typography>
        )}
      </Box>
    )
  }
];

const tblTestCol = [
  {
    field: 'display_label', headerName: 'Service Description', flex: 1,
    renderCell: ({ row }) => {
      const displayValue = row?.service_name
        ? getTruncatedValue(row?.service_name, 40)
        : row?.package_name
          ? getTruncatedValue(row?.package_name, 40)
          : getTruncatedValue(row?.special_test_name, 40);
      return (
        <Typography fontSize={'.8vw'}>{displayValue}</Typography>
      )
    }
  }
]

const tblReviewCol = [
  {
    field: 'duration', headerName: 'Duration', flex: 0.50,
    renderCell: ({ row }) => {
      const displayDuration = `${row?.duration || ""} ${row?.duration_type || ""}`;
      (
        <div style={{ fontSize:'0.8vw'}}>{displayDuration?.length > 15 ?
          <Tooltip placement="top" title={displayDuration} arrow><div>{displayDuration.slice(0, 15) + "..."}</div></Tooltip>
          : displayDuration || "-"}</div>
      )
    }
  },
  {
    field: 'review_date', headerName: 'Review Date & Time', flex: 0.50,
    renderCell: ({ row }) => (<div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "flex-start" }}>
      <div style={{ fontSize: "0.8vw" }}>
        {row?.review_date ? formatDate(row?.review_date) : "-"}
        {row?.appointment_time ? " & " + DateTime.fromFormat(row?.appointment_time, "HH:mm:ss").toFormat("hh:mm a") : "-"}
      </div>
    </div>)
  },
]

const tblPainChartCol = [
  {
    field: 'pain_chart_date', headerName: "Date & Time", flex: 0.333, headerAlign: "center", align: "center",
    renderCell: ({ row }) => {
      let startDate = row?.pain_chart_date ? new Date(row.pain_chart_date) : "";
      let date = DateTime.fromJSDate(startDate).toFormat("dd-MM-yyyy hh:mm a");
      return (<Box component={'div'} fontSize={"0.8vw"}>{date ? (date) : '-'}</Box>)
    }
  },
  {
    field: "pain_level", flex: 0.333, headerName: "Pain Level", headerAlign: "center", align: "center",
    renderCell: ({ row }) => {
      const painItem = painLevelTxt[row?.pain_level];
      return (painItem ? <Box component={'div'} sx={{ display: "flex", flexDirection: "row", gap: "0.5vw" }}>
        <Typography fontSize={'0.8vw'} sx={{ color: painItem.color }}>{getTruncatedValue(row?.pain_level, 10)}</Typography>
        <img className='emedhub_pain_range' src={painItem.image} />
        <Typography fontSize={'0.8vw'} sx={{ color: painItem.color }}>{getTruncatedValue(painItem.status, 10)}</Typography>
      </Box> : "-")
    }
  }
]

const tblExamCol = [
  {
    field: 'systematic_examination', headerName: 'Systematic Examination', flex: 0.333,
    renderCell: ({ row }) => (<Typography fontSize={'.8vw'}>{getTruncatedValue(row?.systematic_examination, 15)}</Typography>)
  },
  {
    field: 'signs', headerName: 'Signs', flex: 0.333,
    renderCell: ({ row }) => (<Typography fontSize={'.8vw'}>{getTruncatedValue(row?.signs, 15)}</Typography>)
  },
  {
    field: 'comments', headerName: 'Comments', flex: 0.333,
    renderCell: ({ row }) => (<Typography fontSize={'.8vw'}>{getTruncatedValue(row?.comments, 15)}</Typography>)
  },
]

const tblAllergyCol = [
  {
    field: "allergy_type", flex: 0.333, headerName: "Allergy Type",
    renderCell: ({ row }) => (
      <Box component={'div'}>
        <Typography fontSize={'0.8vw'}>{row?.allergy_type === 2 ? "Non Medication" : row?.allergy_type === 3 ? "No Allergy" : "Medication"}</Typography>
        <Typography fontSize={'0.8vw'}>{row?.diagnosed_date ? formatDate(row?.diagnosed_date) : "-"}</Typography>
      </Box>
    )
  },
  {
    field: "name", flex: 0.333, headerName: "Allergy Name", headerAlign: "center", align: 'center',
    renderCell: ({ row }) => (<Box component={'div'} fontSize={'0.8vw'}>
      {row?.name ? row?.name?.length > 10 ?
        <Tooltip placement='top' title={row?.name} arrow>
          <div style ={{fontSize: "0.8vw"}}>{row?.name.slice(0, 10) + "..."}</div></Tooltip> :
        row?.name : "-"}
    </Box>)
  },
  {
    field: "is_active", flex: 0.333, headerName: "Status",
    renderCell: ({ row }) => (<Box component={'div'}>
      <Typography color={row?.is_active ? "red" : "orange"} fontSize={'0.8vw'}>{row?.allergy_type === 3 ? '-' : row?.is_active ? "Active" : "Inactive"}</Typography>
    </Box>)
  },
]

const tblObj = {
  "prescription": tblPrescripCol,
  "test": tblTestCol,
  "review_plan": tblReviewCol,
  "pain_scale": tblPainChartCol,
  "examination": tblExamCol,
  "allergies": tblAllergyCol,
}

export const renderNOData = () => {
  return (<Box className="eMed_noDataDiv">
    <p id="eMed_revenueCard_Txt">No Records Found</p>
  </Box>)
}

export const renderTitle = (Title, isSelected, showButton) => {
  return (<Box display={"flex"} alignItems={"center"} gap={"0.5vw"}>
    {isSelected && <DoubleArrowIcon color='error' sx={{ fontSize: '1.2vw' }} />}
    <Typography fontSize={'1vw'} fontWeight={600} color={Colors.SecondaryText}>{Title}</Typography>
    {showButton ? showButton : null}
  </Box>)
}

export const renderIconButton = (title, src, onClick, disabled = false, size = "1.1vw") => (
  <Tooltip placement="top" title={title} arrow>
    <span>
      <IconButton onClick={onClick} disabled={disabled}>
        <Box
          component="img"
          src={src}
          height={size}
          width={size}
          alt={title}
        />
      </IconButton>
    </span>
  </Tooltip>
);

export const ActionButton = ({ text, emed_tid = "saras_doc_notes_btn", sx = {}, size = "small",
  variant = "contained", disabled = false, onClick = () => { }, className = "" }) => (
  <Button
    emed_tid={emed_tid}
    sx={{ textTransform: 'capitalize', ...sx }}
    className={className}
    size={size}
    variant={variant}
    disabled={disabled}
    onClick={onClick}
  >
    {text}
  </Button>
);

export const RenderTextBox = ({ label, content = "", ...props }) => {
  const handleCopy = async () => {
    try {
      // Write both HTML and plain text to clipboard
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([content], { type: 'text/html' }),
          'text/plain': new Blob([content.replace(/<[^>]*>/g, '')], { type: 'text/plain' }),
        }),
      ]);
    } catch (err) {
      console.error("Failed to copy HTML:", err);
    }
  };

  return (
    <Box marginY="0.5dvw">
      <Box fontSize={"0.85vw"} fontWeight={"bold"} color={Colors.SecondaryText}>
        {label}
        {renderIconButton("Copy", ImagePaths.fileIcon.default, handleCopy, false, '1.5vw')}
      </Box>
      {(content) ? (
        <Typography
          my="5px"
          fontSize="0.85dvw"
          className="emed_lastrec_border"
          sx={{
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
          }}
          dangerouslySetInnerHTML={{ __html: content || "-" }}
        />
      ) : (<Typography my="5px" fontSize="0.85dvw" className="emed_lastrec_border">{renderNOData()}</Typography>)}
    </Box>
  )
}

export const RenderChip = ({ list = [], label = "", disable = false, onDelete = undefined, ...props }) => {
  return (
    <Box component={'div'} marginY="0.5dvw">
      {label && <Box fontSize={"0.85vw"} fontWeight={"bold"} color={Colors.SecondaryText} marginY={"5px"}>{label}</Box>}
      <Box className={label ? "emed_lastrec_border" : ""}>
        {list?.length > 0 ? list?.map((item, index) => {
          return (
            <Chip
              className='eMed_chip eMed_Chip_Border'
              size="small"
              label={item.category}
              key={index}
              variant='outlined'
              disabled={disable}
              emed_tid='pat_dlt_cat'
              onDelete={onDelete}
            />
          )
        }) : renderNOData()}
      </Box>
    </Box>)
}

export const RenderTable = ({ field_name, tblLabel, tblData = [], ...props }) => {
  const rows = tblData?.map((item, i) => ({ ...item, sno: i + 1 })) || [];
  const tblColumn = tblObj[field_name]
  return (
    <Box width={"100%"}>
      {tblLabel && <Box fontSize={"0.85vw"} fontWeight={"bold"} color={Colors.SecondaryText} marginY={"5px"}>{tblLabel}</Box>}
      <Box className="eMed_lastTbl_wrapper">
        <DataGrid
          rows={rows}
          columns={tblColumn}
          getRowId={(row) => row.sno}
          getRowHeight={({ model }) => {
            return Array.isArray(model?.tapper_meds) && model.tapper_meds.length > 0 ? 'auto' : 40;
          }}
          headerHeight={30}
          hideFooter
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          localeText={{
            noRowsLabel: "No Records Found",
          }}
          components={{
            NoRowsOverlay: () => (renderNOData()),
          }}
        />
      </Box>
    </Box>
  );
};

export const RenderIcdDiagnosis = ({ label = "", list = [], ...props }) => {
  return (
    <Box component={'div'} marginY="0.5dvw">
      {label && <Box fontSize={"0.85vw"} fontWeight={"bold"} color={Colors.SecondaryText} marginY={"5px"}>{label}</Box>}
      <Box className={label ? "emed_lastrec_border" : ""}>
        {list?.length > 0 ? list?.map((item, index) => {
          return (
            <Box key={index}>
              <Typography fontSize="0.85dvw" color={Colors.SecondaryText}>
                <Box component="span" fontWeight="bold">ICD:</Box> {item?.icd_code || "-"}
              </Typography>
              <Typography fontSize="0.85dvw" fontWeight={"bold"} color={Colors.SecondaryText}>Short Description</Typography>
              <Typography fontSize="0.85dvw">
                {item?.short_desc?.length > 15 ?
                  <Tooltip placement="top" title={item?.short_desc} arrow><div>{item?.short_desc.slice(0, 15) + "..."}</div></Tooltip>
                  : item?.short_desc || "-"}
              </Typography>
            </Box>
          )
        }) : renderNOData()}
      </Box>
    </Box>)
}

export const RenderVitals = ({ label = "", valueobj = {}, ...props }) => {
  return (
    <Box component="div" marginY="0.5dvw">
      {label && (
        <Box fontSize="0.85vw" fontWeight="bold" color={Colors.SecondaryText} marginY="5px">
          {label}
        </Box>
      )}
      <Box className={label ? "emed_lastrec_border" : ""}>
        <Typography fontSize="0.85dvw" mb="0.3vw" color={Colors.SecondaryText}>
          <Box fontSize="0.85dvw" component="span" fontWeight="bold">Height (cm): </Box>
          {valueobj?.height_cm?.length > 15 ? (
            <Tooltip title={valueobj?.height_cm} arrow placement="top">
              <span>{valueobj?.height_cm.slice(0, 15) + "..."}</span>
            </Tooltip>
          ) : (
            valueobj?.height_cm || "-"
          )}
        </Typography>
        <Typography fontSize="0.85dvw" mb="0.3vw" color={Colors.SecondaryText}>
          <Box fontSize="0.85dvw" component="span" fontWeight="bold">Weight (kg): </Box>
          {valueobj?.weight_kg?.length > 15 ? (
            <Tooltip title={valueobj?.weight_kg} arrow placement="top">
              <span>{valueobj?.weight_kg?.slice(0, 15) + "..."}</span>
            </Tooltip>
          ) : (
            valueobj?.weight_kg || "-"
          )}
        </Typography>
        <Typography fontSize="0.85dvw" mb="0.3vw" color={Colors.SecondaryText}>
          <Box fontSize="0.85dvw" component="span" fontWeight="bold">BMI: </Box>
          {valueobj?.bmi?.length > 15 ? (
            <Tooltip title={valueobj?.bmi} arrow placement="top">
              <span>{valueobj?.bmi?.slice(0, 15) + "..."}</span>
            </Tooltip>
          ) : (
            valueobj?.bmi || "-"
          )}
        </Typography>
        <Typography fontSize="0.85dvw" mb="0.3vw" color={Colors.SecondaryText}>
          <Box fontSize="0.85dvw" component="span" fontWeight="bold">BSA: </Box>
          {valueobj?.bsa?.length > 15 ? (
            <Tooltip title={valueobj?.bsa} arrow placement="top">
              <span>{valueobj?.bsa?.slice(0, 15) + "..."}</span>
            </Tooltip>
          ) : (
            valueobj?.bsa || "-"
          )}
        </Typography>
        <Typography fontSize="0.85dvw" mb="0.3vw" color={Colors.SecondaryText}>
          <Box fontSize="0.85dvw" component="span" fontWeight="bold">Systolic BP (mm/Hg): </Box>
          {valueobj?.blood_pressure_systolic?.length > 15 ? (
            <Tooltip title={valueobj?.blood_pressure_systolic} arrow placement="top">
              <span>{valueobj?.blood_pressure_systolic?.slice(0, 15) + "..."}</span>
            </Tooltip>
          ) : (
            valueobj?.blood_pressure_systolic || "-"
          )}
        </Typography>
        <Typography fontSize="0.85dvw" mb="0.3vw" color={Colors.SecondaryText}>
          <Box fontSize="0.85dvw" component="span" fontWeight="bold">Diastolic BP (mm/Hg): </Box>
          {valueobj?.blood_pressure_diastolic?.length > 15 ? (
            <Tooltip title={valueobj?.blood_pressure_diastolic} arrow placement="top">
              <span>{valueobj?.blood_pressure_diastolic?.slice(0, 15) + "..."}</span>
            </Tooltip>
          ) : (
            valueobj?.blood_pressure_diastolic || "-"
          )}
        </Typography>
        <Typography fontSize="0.85dvw" mb="0.3vw" color={Colors.SecondaryText}>
          <Box fontSize="0.85dvw" component="span" fontWeight="bold">{`Temperature: (${valueobj?.temperature_unit || "F"})` }</Box> {valueobj?.temperature || "-"}
        </Typography>
        <Typography fontSize="0.85dvw" mb="0.3vw" color={Colors.SecondaryText}>
          <Box fontSize="0.85dvw" component="span" fontWeight="bold">Pulse Oximetry (%): </Box> {valueobj?.pulse_oximetry || "-"}
        </Typography>
        <Typography fontSize="0.85dvw" mb="0.3vw" color={Colors.SecondaryText}>
          <Box fontSize="0.85dvw" component="span" fontWeight="bold">Oxygen Source: </Box> {valueobj?.oxygen_source || "-"}
        </Typography>
        {valueobj?.oxygen_source === "O2 (With Oxygen)" && <Typography fontSize="0.85dvw" mb="0.3vw" color={Colors.SecondaryText}>
          <Box fontSize="0.85dvw" component="span" fontWeight="bold">Oxygen Support (L/min): </Box> {valueobj?.oxygen_support || "-"}
        </Typography>}
        <Typography fontSize="0.85dvw" mb="0.3vw" color={Colors.SecondaryText}>
          <Box fontSize="0.85dvw" component="span" fontWeight="bold">Waist Circumference (cm): </Box> {valueobj?.waist_circumference || "-"}
        </Typography>
        <Typography fontSize="0.85dvw" mb="0.3vw" color={Colors.SecondaryText}>
          <Box fontSize="0.85dvw" component="span" fontWeight="bold">Random Blood Glucose (mg/dL): </Box> {valueobj?.blood_sugar || "-"}
        </Typography>
        <Typography fontSize="0.85dvw" mb="0.3vw" color={Colors.SecondaryText}>
          <Box fontSize="0.85dvw" component="span" fontWeight="bold">Pulse Rate (/min): </Box> {valueobj?.pulse_rate || "-"}
        </Typography>
        <Typography fontSize="0.85dvw" mb="0.3vw" color={Colors.SecondaryText}>
          <Box fontSize="0.85dvw" component="span" fontWeight="bold">Head Circumference (cm): </Box> {valueobj?.head_circumference || "-"}
        </Typography>
        <Typography fontSize="0.85dvw" mb="0.3vw" color={Colors.SecondaryText}>
          <Box fontSize="0.85dvw" component="span" fontWeight="bold">Gestational Age: </Box> {`${valueobj?.gestational_age_days} days` || "-"} {`${valueobj?.gestational_age_weeks} weeks` || "-"}
        </Typography>
        <Typography fontSize="0.85dvw" mb="0.3vw" color={Colors.SecondaryText}>
          <Box fontSize="0.85dvw" component="span" fontWeight="bold">Last Menstrual Period: </Box> {valueobj?.last_menstrual_period ? formatValue(valueobj?.last_menstrual_period) : "-"}
        </Typography>
        <Typography fontSize="0.85dvw" mb="0.3vw" color={Colors.SecondaryText}>
          <Box fontSize="0.85dvw" component="span" fontWeight="bold">Expected Date of Delivery: </Box> {valueobj?.expected_date_of_delivery ? formatValue(valueobj?.expected_date_of_delivery) : "-"}
        </Typography>
        <Typography fontSize="0.85dvw" mb="0.3vw" color={Colors.SecondaryText}>
          <Box fontSize="0.85dvw" component="span" fontWeight="bold">Comments: </Box>
          {valueobj?.comments?.length > 15 ? (
            <Tooltip title={valueobj?.comments} arrow placement="top">
              <span>{valueobj?.comments?.slice(0, 15) + "..."}</span>
            </Tooltip>
          ) : (
            valueobj?.comments || "-"
          )}
        </Typography>
        <Typography fontSize="0.85dvw" mb="0.3vw" color={Colors.SecondaryText}>
          <Box fontSize="0.85dvw" component="span" fontWeight="bold">Nurse Name: </Box>
          {valueobj?.nurse_name?.length > 25 ? (
            <Tooltip title={valueobj?.nurse_name} arrow placement="top">
              <span>{valueobj?.nurse_name?.slice(0, 25) + "..."}</span>
            </Tooltip>
          ) : (
            valueobj?.nurse_name || "-"
          )}
        </Typography>
        <Typography fontSize="0.85dvw" mb="0.3vw" color={Colors.SecondaryText}>
          <Box fontSize="0.85dvw" component="span" fontWeight="bold">Heart Rate (bpm): </Box> {valueobj?.heart_rate ? formatValue(valueobj?.heart_rate) : "-"}
        </Typography>
        <Typography fontSize="0.85dvw" mb="0.3vw" color={Colors.SecondaryText}>
          <Box fontSize="0.85dvw" component="span" fontWeight="bold">Respiratory Rate (breaths/min): </Box> {valueobj?.respiratory_rate ? formatValue(valueobj?.respiratory_rate) : "-"}
        </Typography>
        <Typography fontSize="0.85dvw" mb="0.3vw" color={Colors.SecondaryText}>
          <Box fontSize="0.85dvw" component="span" fontWeight="bold">Capillary Refill Time (sec): </Box> {valueobj?.capillary_refill_time ? formatValue(valueobj?.capillary_refill_time) : "-"}
        </Typography>
      </Box>
    </Box>
  );
};

export const RenderObstericData = ({ label = "", valueobj = {}, ...props }) => {
  return (
    <Box component="div" marginY="0.5dvw">
      {label && (
        <Box fontSize="0.85vw" fontWeight="bold" color={Colors.SecondaryText} marginY="5px">
          {label}
        </Box>
      )}
      <Box className={label ? "emed_lastrec_border" : ""}>
        <Typography fontSize="0.85dvw" mb="0.3vw" color={Colors.SecondaryText}>
          <Box fontSize="0.85dvw" component="span" fontWeight="bold">PICME: </Box>
          {valueobj?.picme.length > 15 ? (
            <Tooltip title={valueobj?.picme} arrow placement="top">
              <span>{valueobj?.picme.slice(0, 15) + "..."}</span>
            </Tooltip>
          ) : (
            valueobj?.picme || "-"
          )}
        </Typography>
        <Typography fontSize="0.85dvw" mb="0.3vw" color={Colors.SecondaryText}>
          <Box fontSize="0.85dvw" component="span" fontWeight="bold">Gravida: </Box>
          {valueobj?.gravida?.length > 15 ? (
            <Tooltip title={valueobj?.gravida} arrow placement="top">
              <span>{valueobj?.gravida?.slice(0, 15) + "..."}</span>
            </Tooltip>
          ) : (
            valueobj?.gravida || "-"
          )}
        </Typography>
        <Typography fontSize="0.85dvw" mb="0.3vw" color={Colors.SecondaryText}>
          <Box fontSize="0.85dvw" component="span" fontWeight="bold">Para: </Box>
          {valueobj?.para?.length > 15 ? (
            <Tooltip title={valueobj?.para} arrow placement="top">
              <span>{valueobj?.para?.slice(0, 15) + "..."}</span>
            </Tooltip>
          ) : (
            valueobj?.para || "-"
          )}
        </Typography>
        <Typography fontSize="0.85dvw" mb="0.3vw" color={Colors.SecondaryText}>
          <Box fontSize="0.85dvw" component="span" fontWeight="bold">Live Birth: </Box>
          {valueobj?.live_births?.length > 15 ? (
            <Tooltip title={valueobj?.live_births} arrow placement="top">
              <span>{valueobj?.live_births?.slice(0, 15) + "..."}</span>
            </Tooltip>
          ) : (
            valueobj?.live_births || "-"
          )}
        </Typography>
        <Typography fontSize="0.85dvw" mb="0.3vw" color={Colors.SecondaryText}>
          <Box fontSize="0.85dvw" component="span" fontWeight="bold">Abortion: </Box>
          {valueobj?.abortions?.length > 15 ? (
            <Tooltip title={valueobj?.abortions} arrow placement="top">
              <span>{valueobj?.abortions?.slice(0, 15) + "..."}</span>
            </Tooltip>
          ) : (
            valueobj?.abortions || "-"
          )}
        </Typography>
      </Box>
    </Box>
  );
};

// Add new template popup
export const TemplatePopup = ({ open, onClose, templateName, setTemplateName, tempType, onSubmit, loading = false, Title }) => {
  const getTitle = () => {
    switch (tempType) {
      case 'rename': return `Rename ${Title} Template`;
      default: return `Add New ${Title} Template`;
    }
  };

  return (
    <Modal open={open} sx={{ border: "none", outline: "none" }}>
      <Box className='eMed_Doc_Notes_Prescrip_Fav_Pop_Up' sx={{ height: "10vw", margin: "auto" }}>
        <Box display="flex" justifyContent="space-between" p="0.5vw" alignItems="center">
          <Typography fontSize="0.9vw" fontWeight={600}> {getTitle()}</Typography>
          <Button className='Common_Btn_Min_Width' onClick={() => { onClose() }}>
            <Box component="img" src={ImagePaths?.LabCloseButton?.default} height="1.1vw" width="1.1vw" />
          </Button>
        </Box>

        <Box ml="2.5vw">
          <TextField
            size="small"
            sx={{ width: '20vw' }}
            autoComplete='off'
            label="Enter Template Name*"
            inputProps={{ maxLength: 75 }}
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
          />
        </Box>

        <Box display="flex" justifyContent="center" alignItems="center" height="3vw">
          <Button
            sx={{ height: '2vw', width: '5vw', textTransform: 'capitalize', mr: '1vw' }}
            variant="outlined"
            onClick={() => setTemplateName('')}
          >
            Clear
          </Button>
          <Button
            sx={{ height: '2vw', width: '5vw', textTransform: 'capitalize' }}
            variant="contained"
            onClick={onSubmit}
            disabled={loading}
          >
            {tempType == 'rename' ? "Update" : "Add"}  
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

// Suggestion popup
export const SuggestionPopup = ({ open = false, onClose, Title, url, postKey, errorMessage, successMessage, ...props }) => {
  const [state, setState] = useState({
    suggestionList: [],
    page: 0,
    rowsPerPage: 10,
    isEditClked: false,
    editIndex: null,
    editData: {},
    saveClked: false,
    deletePopup: false,
    selectedId: null,
    removeData: false,
  });

  const updateState = (updates) => {
    setState((prev) => ({ ...prev, ...updates }));
  };

  // Fetch suggestion data
  useEffect(() => {
    if (url) {
      getSuggestionData();
    }
  }, [url]);

  const getSuggestionData = () => {
    if (!url) return;
    let apiUrl = `${Serviceurls[url]}?search_key=`
    if (props.field_name === "allergies") {
      apiUrl += props.stateName === "comments2" ? `&non_medication_allergy= ` : `&medication_allergy= `
    }
    if (props.isNewLayout) {
      apiUrl += `&system_name= ${props.systemName}`
    }
    RestAPIService.getAllTwo(apiUrl)
      .then((response) => {
        if (response.data.status === 'success') {
          updateState({ suggestionList: response.data.data || [] });
        } else {
          errorMessage(response.data.message);
        }
      })
      .catch((error) => {
        errorMessage(error.message);
      });
  };

  const onEditHandler = (item, index) => {
    updateState({
      isEditClked: true,
      editIndex: index,
      editData: item,
    });
  };

  const handleEditor = (stateKey, value) => {
    updateState({
      editData: {
        ...state.editData,
        [stateKey]: value || '',
      },
    });
  };

  const onCancelHandler = () => {
    updateState({
      isEditClked: false,
      editIndex: null,
      editData: {},
    });
  };

  const onDeleteHandler = (item) => {
    updateState({
      deletePopup: true,
      selectedId: item?.id,
    });
  };

  const popupClose = () => {
    updateState({
      deletePopup: false,
      selectedId: null,
      removeData: false,
    });
  };

  const updateSuggestionData = useCallback(() => {
    updateState({ saveClked: true });

    const data = {
      id: state?.editData?.id || null,
      [postKey]: state.editData[postKey],
    };

    if (props.field_name === "allergies") {
      data[props.postKey2] = state.editData[props.postKey2];
    }
    if (props.isNewLayout) {
      data["system_name"] = props.systemName || "";
      data["custom_name"] = Title;
    }

    RestAPIService.updateWithOutId(data, Serviceurls[url])
      .then((response) => {
        updateState({ saveClked: false });
        if (response.data.status === 'success') {
          successMessage(response.data.message);
          onCancelHandler();
          getSuggestionData();
        } else {
          errorMessage(response.data.message);
        }
      })
      .catch((error) => {
        updateState({ saveClked: false });
        errorMessage(error?.response?.data?.message || error?.message);
      });
  }, [state?.editData?.id, state.editData, postKey, url, successMessage, errorMessage]);

  const deleteSuggestionData = () => {
    updateState({ removeData: true });
    let apiUrl = `${Serviceurls[url]}?id=${state.selectedId}`;
    if (props.field_name === "allergies") {
      apiUrl += props.stateName === "comments2" ? `&non_medication_allergy= ` : `&medication_allergy= `
    }
    if (props?.isNewLayout) {
      apiUrl += `&system_name=${props.systemName}`
    }
    RestAPIService.delete(apiUrl)
      .then((response) => {
        if (response?.data?.status === 'success') {
          successMessage(response?.data?.message);
          popupClose();
          getSuggestionData();
        }
      })
      .catch((error) => {
        updateState({ removeData: false });
        errorMessage(error?.response?.data?.message || error.message);
      });
  };

  const renderButton = (title, imgSrc, onClick, disable = false) => (
    <Tooltip title={title} placement="top" arrow>
      <Button className='eMed_usrconf_btn' onClick={onClick} disabled={disable}>
        <img src={imgSrc} alt={title} style={{ width: '1.5vw', height: '1.5vw' }} />
      </Button>
    </Tooltip>
  );

  const { suggestionList, page, rowsPerPage } = state;
  const paginatedRows = suggestionList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <Box className='eMed_Doc_Notes_Prescrip_Fav_Pop_Up' sx={{ height: '10vw', margin: 'auto' }}>
        <Box display='flex' justifyContent='space-between' p='0.5vw' alignItems='center'>
          <Typography fontSize='0.9vw' fontWeight={600}>
            {`${Title} Suggestion`}
          </Typography>
          <Button className='Common_Btn_Min_Width' onClick={onClose}>
            <Box component='img' src={ImagePaths?.LabCloseButton?.default} height='1.1vw' width='1.1vw' />
          </Button>
        </Box>

        <Box>
          <TableContainer sx={{ overflowY: 'scroll', height: '40vw' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '80%' }} className='eMed_ABill_Head'>
                    Suggestion
                  </TableCell>
                  <TableCell sx={{ width: '20%' }} className='eMed_ABill_Head'>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map((row, idx) => (
                  <TableRow key={row.id}>
                    <TableCell style={{ whiteSpace: 'pre-wrap' }}>
                      {state.isEditClked && state.editIndex === idx ? (
                        <SarasToolsFieldWithMic
                          key={row.id}
                          EditorRef={state?.editData[postKey] || ''}
                          EditorDisable={state.editIndex === null}
                          saveData={handleEditor.bind(this)}
                          isFrom='suggestEdit'
                          minHeight="14vh"
                          overallHeight="auto"
                          stateName={postKey}
                        />
                      ) : (
                        <div dangerouslySetInnerHTML={{ __html: row[postKey] }} />
                      )}
                    </TableCell>
                    <TableCell>
                      {state.isEditClked && state.editIndex === idx ? (
                        <Box display="flex" gap="0.625dvw" justifyContent="center" alignItems="center">
                          {renderButton('Save', ImagePaths.TickEnable.default, updateSuggestionData, state.saveClked)}
                          {renderButton('Cancel', ImagePaths.RejectEnable.default, onCancelHandler)}
                        </Box>
                      ) : (
                        <Box display="flex" gap="0.625dvw" justifyContent="center" alignItems="center">
                          {renderButton('Edit', ImagePaths.Edit.default, () => onEditHandler(row, idx))}
                          {renderButton('Delete', ImagePaths.Delete.default, () => onDeleteHandler(row), state.deletePopup)}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {suggestionList?.length === 0 && <div className='eMed_AB_NoRecords'>No Records Found</div>}
          </TableContainer>
        </Box>

        <TablePagination
          component='div'
          id='eMed_tbl_pagination'
          rowsPerPageOptions={[10, 20, 30]}
          count={suggestionList?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => updateState({ page: newPage })}
          onRowsPerPageChange={(event) => updateState({ rowsPerPage: +event.target.value, page: 0 })}
        />
      </Box>

      {state.deletePopup && (
        <DeletePopup
          DeletTitle="Are you sure you want to delete?"
          deleteAlertPopupClose={popupClose}
          removeData={deleteSuggestionData}
          disable={state.removeData}
        />
      )}
    </Dialog>
  );
};